import { inject, Injectable, signal } from '@angular/core';
import {
	aiInsightsAllowedEmails,
	ENVIRONMENT,
	MenuPointService,
	NavVariant,
	Role,
} from '@agilox/common';
import { forkJoin, Observable, of, switchMap, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MenuPoint } from '@agilox/ui-common';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserStoreService } from '@user-store';
import { MAX_BOARDS } from '../../../../menupoints/boards/create/consts/dashboards-consts';
import { BoardConfig } from '../../../interfaces/board-config.interface';
import { DashboardsSubMenuUrl } from '../../enums/dashboards-submenu.enum';
import { AppsettingsService } from '../appsettings/appsettings.service';
import { analyticsMenuPoints } from '../authentication/analytics-menupoints';

@Injectable({ providedIn: 'root' })
export class AnalyticsMenuPointService extends MenuPointService {
	private appsettingsService = inject(AppsettingsService);
	private environment = inject(ENVIRONMENT);
	private userStoreService = inject(UserStoreService);

	private reloadTrigger = signal<boolean>(false);

	private reloadTrigger$ = toObservable(this.reloadTrigger);

	override getMenuPointsByCategory(type: NavVariant, role?: Role): MenuPoint[] {
		let menuPoints = super.getMenuPointsByCategory(type, role);
		menuPoints = this.filterAiInsights(menuPoints);

		const dashboardMenu = this.findMenuPoint(menuPoints, analyticsMenuPoints.dashboard.url);
		if (dashboardMenu) {
			dashboardMenu.submenuLoader = () => this.loadBoardMenuItems();
		}

		return menuPoints;
	}

	private findMenuPoint(menuPoints: MenuPoint[], url: string): MenuPoint | undefined {
		for (const menuPoint of menuPoints) {
			if (menuPoint.url === url) {
				return menuPoint;
			}
			if (menuPoint.submenus?.length) {
				const found = this.findMenuPoint(menuPoint.submenus, url);
				if (found) {
					return found;
				}
			}
		}
		return undefined;
	}

	private filterAiInsights(menuPoints: MenuPoint[]) {
		const email = this.userStoreService.currentUser()?.email;
		if (
			this.environment.agentApi &&
			email &&
			aiInsightsAllowedEmails.includes(email.toLowerCase())
		) {
			return menuPoints;
		}
		const aiInsightsUrl = analyticsMenuPoints.aiInsights.url;
		return menuPoints
			.filter((mp) => mp.url !== aiInsightsUrl)
			.map((mp) =>
				mp.submenus
					? { ...mp, submenus: mp.submenus.filter((sub) => sub.url !== aiInsightsUrl) }
					: mp
			);
	}

	getMenuPointsWithSubmenus(type: NavVariant, role?: Role): Observable<MenuPoint[]> {
		return this.reloadTrigger$.pipe(
			switchMap(() => {
				const menuPoints = this.getMenuPointsByCategory(type, role);
				return this.loadAllSubmenus(menuPoints);
			})
		);
	}

	reloadMenuPoints(): void {
		this.reloadTrigger.set(!this.reloadTrigger());
	}

	private loadBoardMenuItems(): Observable<MenuPoint[]> {
		return this.appsettingsService.getUserAppSettings().pipe(
			map((appSettings) => {
				const boards: BoardConfig[] = appSettings.boards || [];

				const dynamicMenus: MenuPoint[] = boards.map((board) => ({
					name: board.name,
					url: `${DashboardsSubMenuUrl[board.type]}/${board.id}`,
					roles: [],
				}));

				const isMaxBoardsReached = dynamicMenus.length >= MAX_BOARDS;
				dynamicMenus.push({
					name: this.translate.instant('general.menu.create_new_board'),
					url: DashboardsSubMenuUrl.createboard,
					icon: 'x_plus',
					roles: [],
					disabled: isMaxBoardsReached,
					tooltip: isMaxBoardsReached
						? this.translate.instant('board.create.max_boards_reached')
						: '',
				});

				return dynamicMenus;
			})
		);
	}

	private loadAllSubmenus(menuPoints: MenuPoint[]): Observable<MenuPoint[]> {
		if (menuPoints.length === 0) {
			return of(menuPoints);
		}

		return forkJoin(menuPoints.map((menuPoint) => this.loadMenuPoint(menuPoint)));
	}

	private loadMenuPoint(menuPoint: MenuPoint): Observable<MenuPoint> {
		if (menuPoint.submenuLoader) {
			return this.loadSubmenu(menuPoint);
		}
		if (menuPoint.submenus?.length) {
			return this.loadAllSubmenus(menuPoint.submenus).pipe(
				map((loaded) => {
					menuPoint.submenus = loaded;
					return menuPoint;
				})
			);
		}
		return of(menuPoint);
	}

	private loadSubmenu(menuPoint: MenuPoint): Observable<MenuPoint> {
		return menuPoint.submenuLoader().pipe(
			take(1),
			tap((loadedSubmenus) => {
				menuPoint.submenus = loadedSubmenus;
			}),
			map(() => menuPoint)
		);
	}
}
